/* eslint-disable no-console */

import { captureMediaLoadError } from 'common/modules/media/captureMediaLoadError';
import { isAbortError } from 'common/modules/media/isAbortError';
import { seconds2timestring } from 'common/utils/seconds2timestring';
import { I18nContext, Translate } from 'i18n';
import {
  BaseSyntheticEvent,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { useLessonPause } from 'students/views/shared/hooks';
import styled from 'styled-components';
import DefaultPlayButton, { IProps as IPlayButtonProps } from './PlayButton';

type tTimeVariant = 'fulltime' | 'countdown' | 'none';

export interface IProps extends Pick<IPlayButtonProps, 'size'> {
  src: string | undefined;
  timeVariant?: tTimeVariant;
  withDurationFix?: boolean;
  className?: string;
  buttonClassName?: string;
  playButtonComponent?: React.FC<IPlayButtonProps>;
  autoplay?: boolean;
}

export type tRef = HTMLMediaElement | null;

const isSafari = !!navigator.userAgent.match(/Version\/(\d+[\d.]+\d)+.*Safari/);
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

const LOAD_TIMEOUT_MS = 15000;

/** `HTMLMediaElement.NETWORK_LOADING` — not exposed as a constant by jsdom. */
const NETWORK_LOADING = 2;

export const AudioPlayer = forwardRef<tRef, IProps>(function (
  {
    src,
    withDurationFix,
    className,
    timeVariant,
    buttonClassName,
    playButtonComponent,
    autoplay,
    size
  },
  forwardedRef
) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [canPlay, setCanPlay] = useState(false);
  const [hasError, setHasError] = useState(false);
  const audioElement = useRef<tRef>(null);
  const intentToPlayRef = useRef(false);
  const loadTimeoutRef = useRef<number | null>(null);
  // const combinedAudioRef = useCombinedRefs<HTMLMediaElement>(forwardedRef, audioElement);
  useImperativeHandle<tRef, tRef>(forwardedRef, () => audioElement.current);

  useContext(I18nContext);

  const clearLoadTimeout = useCallback(() => {
    if (loadTimeoutRef.current) {
      window.clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearLoadTimeout, [clearLoadTimeout]);

  // A queued "play once ready" intent from the previous clip must not auto-play this one,
  // and neither the loading state nor the previous clip's error may leak across.
  useEffect(() => {
    intentToPlayRef.current = false;
    clearLoadTimeout();
    setIsLoading(false);
    setHasError(false);
    setCanPlay(false);
  }, [src, clearLoadTimeout]);

  const playAudio = useCallback(
    (element: HTMLMediaElement) => {
      const playPromise: Promise<void> | undefined = element.play();
      playPromise?.catch((err: unknown) => {
        console.error('playing not allowed', err);
        setIsPlaying(false);
        setIsLoading(false);
        // Whatever stops the loading state must also disarm the timeout, or it fires a
        // bogus 'no-canplay-timeout' report 15s later and burns this URL's one report.
        clearLoadTimeout();
        // An interruption would burn that same one report and hide the real rejection.
        if (isAbortError(err)) return;
        captureMediaLoadError(err, {
          kind: 'audio',
          engine: 'html5-audio',
          src,
          context: 'play-rejected'
        });
      });
    },
    [src, clearLoadTimeout]
  );

  const togglePlay = useCallback(
    (shouldPlay: boolean, element: HTMLMediaElement | null) => {
      if (!element) return;
      if (shouldPlay) {
        playAudio(element);
      } else {
        element.pause();
      }
    },
    [playAudio]
  );
  const armLoadTimeout = useCallback(() => {
    clearLoadTimeout();
    loadTimeoutRef.current = window.setTimeout(() => {
      loadTimeoutRef.current = null;
      intentToPlayRef.current = false;
      setIsLoading(false);
      captureMediaLoadError(new Error('HTML5 audio never became playable'), {
        kind: 'audio',
        engine: 'html5-audio',
        src,
        context: 'no-canplay-timeout'
      });
    }, LOAD_TIMEOUT_MS);
  }, [clearLoadTimeout, src]);

  // HANDLERS
  const handleButtonClick = useCallback(() => {
    const element = audioElement.current;
    if (!element) return;

    if (canPlay) {
      // `isPlaying` is driven by the element's play/pause events, not optimistically
      togglePlay(element.paused, element);
      return;
    }

    // A tap already asked for playback and the element is still fetching: `load()` would
    // abort that fetch and restart from zero (tap-tap-tap = never plays). Re-issuing
    // `play()` inside the gesture keeps the request alive instead.
    if (intentToPlayRef.current && element.networkState === NETWORK_LOADING) {
      setIsLoading(true);
      armLoadTimeout();
      playAudio(element);
      return;
    }

    // Not ready yet (e.g. preload="none" on iOS Safari). `play()` is called inside the
    // gesture on purpose: it forces the fetch whatever `preload` says and lifts WebKit's
    // per-element user-gesture restriction. `intentToPlayRef` covers a late `canplay`.
    setHasError(false);
    intentToPlayRef.current = true;
    setIsLoading(true);
    armLoadTimeout();
    element.load();
    playAudio(element);
  }, [canPlay, togglePlay, armLoadTimeout, playAudio]);

  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleAudioPlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handleAudioPause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  /**
   * `stalled`/`waiting`: data stopped arriving — drop the loader so the tap can be
   * repeated; the play intent is kept, so a late `canplay` still produces sound.
   *
   * `suspend` is deliberately NOT wired here: per the HTML spec it only means "the UA is
   * intentionally not fetching right now" and it fires right after `load()` under
   * `preload="none"` (our iOS config below) — treating it as failure cleared the spinner
   * mid-fetch.
   */
  const handleLoadInterrupted = useCallback(() => {
    clearLoadTimeout();
    setIsLoading(false);
  }, [clearLoadTimeout]);

  const handleAudioTimeUpdate = useCallback((e: BaseSyntheticEvent) => {
    setCurrentTime(e.target.currentTime || 0);
  }, []);

  const handleAudioMetaLoaded = useCallback((e: BaseSyntheticEvent) => {
    setDuration(e.target.duration || 0);
  }, []);

  // fix for duration. described here: https://stackoverflow.com/a/52375280
  async function fixDurationCalculation(e: BaseSyntheticEvent) {
    if (e.target && e.target.duration === Infinity) {
      e.persist && e.persist();
      const { target } = e;
      try {
        while (target.duration === Infinity) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => {
            setTimeout(r, 0);
          });
          target.currentTime = 10000000 * Math.random();
        }
      } catch (error) {
        console.log(error);
      }
      target.currentTime = 0;
      setDuration(target.duration || 0);
    }
    setIsLoading(false);
    setCanPlay(true);
    setHasError(false);
  }
  const handleCanPlayAudio = useCallback(
    async (e: BaseSyntheticEvent) => {
      clearLoadTimeout();
      // Read-and-clear the intent synchronously: `fixDurationCalculation` awaits, so two
      // overlapping `canplay` events would otherwise both observe it and play twice.
      const shouldPlay = intentToPlayRef.current;
      intentToPlayRef.current = false;

      if (withDurationFix) {
        await fixDurationCalculation(e);
      } else {
        setCanPlay(true);
        setIsLoading(false);
        setHasError(false);
      }

      if (shouldPlay && audioElement.current) playAudio(audioElement.current);
    },
    [withDurationFix, clearLoadTimeout, playAudio]
  );

  const handleError = useCallback(
    (_error: unknown) => {
      // @ts-expect-error mediaError is not typed on the synthetic event target
      const mediaError = _error?.target?.error;
      console.info('AUDIO LOAD ERROR', mediaError);
      clearLoadTimeout();
      intentToPlayRef.current = false;
      captureMediaLoadError(mediaError ?? new Error('HTML5 audio load error'), {
        kind: 'audio',
        engine: 'html5-audio',
        src
      });
      setHasError(true);
      setCanPlay(false);
      setIsPlaying(false);
      setIsLoading(false);
    },
    [src, clearLoadTimeout]
  );
  // HANDLERS END

  function getTimeToDisplay(): string {
    switch (timeVariant) {
      case 'countdown':
        return `${formatTime(duration - currentTime)}`;
      case 'none':
        return '';
      case 'fulltime':
      default:
        return `${formatTime(currentTime)} / ${formatTime(duration)}`;
    }
  }

  const stopPlaying = useCallback(() => {
    // Drop any queued "play once ready" intent: an explicit stop (lesson/modal pause) must
    // not be resurrected by a `canplay` that lands afterwards.
    intentToPlayRef.current = false;
    setIsPlaying(false);
    audioElement?.current?.pause();
  }, [setIsPlaying]);

  useLessonPause(stopPlaying);

  if (!src) return null;

  const timeToDisplay = getTimeToDisplay();
  const PlayButton = playButtonComponent || DefaultPlayButton;

  return (
    <SPlayerDiv className={className}>
      <PlayButton
        className={buttonClassName}
        isPlaying={isPlaying}
        onClick={handleButtonClick}
        hasError={hasError}
        isLoading={isLoading}
        size={size}
      />
      {canPlay && !!timeToDisplay && <SPlayerTime>{timeToDisplay}</SPlayerTime>}
      {hasError && !isLoading && (
        <SMessage>
          <Translate str="frontend.media_player.audio_player.reload_message" />
        </SMessage>
      )}
      <audio
        preload={isSafari && isIOS ? 'none' : 'auto'}
        ref={audioElement}
        onEnded={handleAudioEnded}
        onTimeUpdate={handleAudioTimeUpdate}
        onLoadedMetadata={handleAudioMetaLoaded}
        onCanPlay={handleCanPlayAudio}
        onPlay={handleAudioPlay}
        onPause={handleAudioPause}
        onWaiting={handleLoadInterrupted}
        onStalled={handleLoadInterrupted}
        onError={handleError}
        autoPlay={autoplay}
      >
        <source src={src} />
      </audio>
    </SPlayerDiv>
  );
});

const SPlayerDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  audio {
    visibility: hidden;
  }
`;

const SPlayerTime = styled.span`
  color: #666;
  font-size: 1.5rem;
  line-height: 1.2;
  margin: 0 10px;
`;

const SMessage = styled.span`
  color: #666;
  font-size: 1.1rem;
  margin: 0 10px;
`;

function formatTime(timeInSeconds: number | undefined, ceilSeconds?: boolean) {
  if (timeInSeconds === Infinity) return '-:--';

  return seconds2timestring(timeInSeconds || 0, ceilSeconds);
}
